/**
 * SpendByCategory.jsx — category × month spend grid.
 *
 * Reads bank transactions directly rather than `expenses`, so it shows real
 * outflow including charges nobody has reviewed yet. Transfers between your own
 * accounts and credit card payments are excluded server-side.
 */
import { useMemo, useState } from 'react';
import {
  Box, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Typography, ToggleButtonGroup, ToggleButton, Skeleton,
} from '@mui/material';
import { fmtC } from '../../lib/utils';
import { useBankSpend, useBankSpendByJob } from '../../hooks/usePlaid';

function monthLabel(ym) {
  const [y, m] = ym.split('-');
  return new Date(Number(y), Number(m) - 1, 1)
    .toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
}

// Unreviewed spend falls back to Plaid's own category, which arrives as
// GENERAL_MERCHANDISE. Left raw it sits in the same column as our own
// "Equipment" and "Software" and reads like two different systems.
function prettyCategory(c) {
  if (!c || !/^[A-Z0-9_]+$/.test(c)) return c;
  const words = c.replaceAll('_', ' ').toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export default function SpendByCategory() {
  const [months, setMonths] = useState(6);
  const { data: rows = [], isLoading } = useBankSpend(months);
  const { data: byJob = [] } = useBankSpendByJob();

  // Pivot the flat (month, category, total) rows into a grid.
  const { monthKeys, categories, cell, monthTotals, catTotals, grand } = useMemo(() => {
    const mSet = new Set(), cSet = new Set(), map = new Map();
    const mTot = new Map(), cTot = new Map();
    let g = 0;
    for (const r of rows) {
      const total = Number(r.total) || 0;
      mSet.add(r.month); cSet.add(r.category);
      map.set(`${r.month}|${r.category}`, total);
      mTot.set(r.month, (mTot.get(r.month) || 0) + total);
      cTot.set(r.category, (cTot.get(r.category) || 0) + total);
      g += total;
    }
    return {
      monthKeys: [...mSet].sort().reverse(),
      categories: [...cSet].sort((a, b) => (cTot.get(b) || 0) - (cTot.get(a) || 0)),
      cell: map, monthTotals: mTot, catTotals: cTot, grand: g,
    };
  }, [rows]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, height: '100%', minHeight: 0, overflow: 'auto' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={months} onChange={(_, v) => v && setMonths(v)}>
          {[3, 6, 12].map((n) => (
            <ToggleButton key={n} value={n} sx={{ textTransform: 'none', px: 2 }}>{n} mo</ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" sx={{ color: 'text.secondary', ml: 'auto' }}>
          Excludes transfers between your own accounts and credit card payments
        </Typography>
      </Box>

      {isLoading ? (
        <Skeleton variant="rounded" height={280} />
      ) : !rows.length ? (
        <Paper sx={{ p: 6, textAlign: 'center', color: 'text.secondary' }}>
          <Typography variant="body2">No spend recorded yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                {monthKeys.map((m) => (
                  <TableCell key={m} align="right" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                    {monthLabel(m)}
                  </TableCell>
                ))}
                <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((c) => (
                <TableRow key={c} hover>
                  <TableCell>{prettyCategory(c)}</TableCell>
                  {monthKeys.map((m) => {
                    const v = cell.get(`${m}|${c}`);
                    return (
                      <TableCell key={m} align="right" sx={{ color: v ? 'text.primary' : 'text.disabled' }}>
                        {v ? fmtC(v) : '—'}
                      </TableCell>
                    );
                  })}
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtC(catTotals.get(c))}</TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                <TableCell>Total</TableCell>
                {monthKeys.map((m) => (
                  <TableCell key={m} align="right">{fmtC(monthTotals.get(m))}</TableCell>
                ))}
                <TableCell align="right">{fmtC(grand)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!!byJob.length && (
        <>
          <Typography variant="overline" sx={{ color: 'text.secondary', mt: 1 }}>
            Spend assigned to jobs
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 600 }}>Job</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Charges</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {byJob.map((j) => (
                  <TableRow key={j.job_id} hover>
                    <TableCell>{j.job_title || j.job_id}</TableCell>
                    <TableCell align="right">{j.count}</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>{fmtC(j.total)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Box>
  );
}
